
import {
  useMemo,
  useState,
  useEffect,
  Fragment,
} from 'react';
import type {
  ChangeEvent,
} from 'react';
import {
  useAlerts,
  useAuthentication,
  useFeedbacks,
} from '@dinosave/core/stores';
import type {
  FeedbackEntity,
} from '@dinosave/core/types';
import {
  validateEmail,
} from '@dinosave/core/validations';
import {
  Modal,
} from '@/components/Modals';
import type {
  ModalProps,
} from '@/components/Modals';
import Input from '@/ui/components/Input';
import Text from '@/ui/components/Text';
import Textarea from '@/ui/components/Textarea';

import styles from './RequestFeatureModal.module.css';

const RequestFeatureModal = (props: ModalProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    isOpen,
    onClose,
    ...rest
  } = props;

  // ##########################################################################
  // STORES
  // ##########################################################################

  const addError = useAlerts(s => s.addError);

  const authLoading = useAuthentication(s => s.authLoading);
  const authenticatedUser = useAuthentication(s => s.authenticatedUser);
  const authenticatedUserId = authenticatedUser?.id;
  const authorized = !authLoading && !!authenticatedUserId;

  const createFeedback = useFeedbacks(s => s.createFeedback);

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [descriptionInvalid, setDescriptionInvalid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // ##########################################################################
  // MEMOIZATION
  // ##########################################################################

  const isEmailValid = useMemo(() => (
    authorized || validateEmail(email)
  ), [email, authorized]);

  const isDescriptionValid = useMemo(() => (
    description.length > 0
  ), [description]);

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmailInvalid(false);
    setEmail(e.target.value);
  };

  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setDescriptionInvalid(false);
    setDescription(e.target.value);
  };

  const onSubmit = async () => {
    if (
      loading ||
      !isEmailValid ||
      !isDescriptionValid
    ) {
      setEmailInvalid(!isEmailValid);
      setDescriptionInvalid(!isDescriptionValid);
      return;
    }

    setLoading(true);

    try {
      const feedback = {
        email,
        category: 'feature_request',
        description: description.trim(),
      } as FeedbackEntity;

      await createFeedback(feedback);

      setSuccess(true);
    } catch (e) {
      setLoading(false);
      addError(e);
    }
  };

  const handleSubmit = !success && onSubmit;

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  useEffect(() => {
    if (
      !isOpen
    ) return;

    setEmail('');
    setDescription('');
    setEmailInvalid(false);
    setDescriptionInvalid(false);
    setSuccess(false);
    setLoading(false);
  }, [isOpen]);

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <Modal
      {...rest}
      title="Feature request"
      isOpen={isOpen}
      cancelText="Close"
      confirmText="Submit"
      onClose={onClose}
      onCancel={onClose}
      onConfirm={handleSubmit}
      className={styles.requestFeatureModal}
      contentClassName={styles.content}
    >
      {success ? (
        <Text
          kind="paragraph"
          size="regular"
          color="neutral-500"
          className={styles.success}
        >
          Thanks for your feedback!
          People like you help make Dinosave better 😊
        </Text>
      ) : (
        <Fragment>
          <Text
            kind="paragraph"
            size="regular"
            color="neutral-500"
            className={styles.description}
          >
            Got any ideas on how to improve Dinosave?
          </Text>

          <Textarea
            value={description}
            onChange={handleDescriptionChange}
            invalid={descriptionInvalid}
            placeholder="I want to see Dino roar once a sale is found!"
          />

          {!authorized && (
            <div className={styles.email}>
              <Text kind="label">
                EMAIL
              </Text>

              <Input
                value={email}
                onChange={handleEmailChange}
                invalid={emailInvalid}
                placeholder="Enter your Email"
                disabled={loading}
              />
            </div>
          )}
        </Fragment>
      )}
    </Modal>
  );
};

export default RequestFeatureModal;
